import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PositionHistoryPage from './pages/position-history-page';
import PositionHistoryDetail from './pages/position-history-detail';
import Settings from './pages/Settings';
import Favorites from './pages/Favorites';
import HighRoeTrades from './pages/HighRoeTrades';
import FounderSettings from './pages/founder-settings';
import SymbolManagement from './pages/symbol-management';
import WeeklyStats from './pages/weekly-stats';
import OpenOrdersPage from './pages/open-orders';
import AdminActionsPage from './pages/admin-actions';
import CheckinPage from './pages/checkin';
import EngineDashboard from './pages/EngineDashboard';
import EngineSignals from './pages/EngineSignals';
import EnginePositions from './pages/EnginePositions';
import EngineRuns from './pages/EngineRuns';
import SingleSymbolLearning from './pages/SingleSymbolLearning';
import DegoScanPage from './pages/dego-scan';
import AtrFibonacciScanPage from './pages/atr-fibonacci-scan';
import TylerScanPage from './pages/tyler-scan';
import RsiScanPage from './pages/rsi-scan';
import VpSupertrendPage from './pages/vp-supertrend';
import V18InspectorPage from './pages/v18-inspector';
import SymbolGridPage from './pages/SymbolGridPage';
import MarketSymbolsPage from './pages/MarketSymbolsPage';
import KronosStatusPage from './pages/KronosStatusPage';
import HyperliquidWalletMonitor from './pages/HyperliquidWalletMonitor';
import api, { AUTH } from '../infrastructure/config/api';
import { isAuthenticated } from '../infrastructure/utils/auth';

// Create a theme instance
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
});

// Custom loading component
const LoadingScreen = () => (
  <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
    <LinearProgress />
    <Box sx={{ m: 'auto', textAlign: 'center' }}>
      <CircularProgress />
      <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  </Box>
);
// Protected route component
const ProtectedRoute = ({ children }) => {
  const [authStatus, setAuthStatus] = useState({
    isAuthenticated: false,
    isLoading: true,
  });
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a token first
        if (!isAuthenticated()) {
          throw new Error('No token found');
        }

        // Verify token with server
        const response = await api.get(AUTH.CHECK_AUTH);
        setAuthStatus({
          isAuthenticated: response.data.authenticated,
          isLoading: false,
        });
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthStatus({
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    checkAuth();
  }, [location]);

  if (authStatus.isLoading) {
    return <LoadingScreen />;
  }

  return authStatus.isAuthenticated ? children : <Navigate to="/login" state={{ from: location }} replace />;
};

// Public route that redirects to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (isAuthenticated()) {
          const response = await api.get(AUTH.CHECK_AUTH);
          setIsAuth(response.data.authenticated);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return <LoadingScreen />;
  }

  return isAuth ? <Navigate to="/dashboard" replace /> : children;
};

const DevkOnlyRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isDevkAdmin = user?.role === 'admin' && user?.username === 'devk';

  return isDevkAdmin ? children : <Navigate to="/dashboard" replace />;
};

const KhanhOnlyRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user?.username === 'khanh' ? children : <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          overflow: 'auto',
        }}
      >
        <Router basename={process.env.PUBLIC_URL || '/'}>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard/symbols"
              element={
                <ProtectedRoute>
                  <KhanhOnlyRoute><SymbolGridPage /></KhanhOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/market-symbols"
              element={
                <ProtectedRoute>
                  <MarketSymbolsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/position-history">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <DevkOnlyRoute>
                      <PositionHistoryPage />
                    </DevkOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":symbol"
                element={
                  <ProtectedRoute>
                    <DevkOnlyRoute>
                      <PositionHistoryDetail />
                    </DevkOnlyRoute>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hyperliquid-wallet-monitor"
              element={
                <ProtectedRoute>
                  <HyperliquidWalletMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <Favorites />
                </ProtectedRoute>
              }
            />
            <Route
              path="/high-roe"
              element={
                <ProtectedRoute>
                  <HighRoeTrades />
                </ProtectedRoute>
              }
            />
            <Route
              path="/founder-settings"
              element={
                <ProtectedRoute>
                  <FounderSettings />
                </ProtectedRoute>
              }
            />
            <Route
            path="/symbol-management"
              element={
                <ProtectedRoute>
                  <SymbolManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/weekly-stats"
              element={
                <ProtectedRoute>
                  <WeeklyStats />
                </ProtectedRoute>
              }
            />
            <Route
              path="/open-orders"
              element={
                <ProtectedRoute>
                  <OpenOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-actions"
              element={
                <ProtectedRoute>
                  <DevkOnlyRoute>
                    <AdminActionsPage />
                  </DevkOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkin"
              element={
                <ProtectedRoute>
                  <CheckinPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/engine/dashboard"
              element={
                <ProtectedRoute>
                  <EngineDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/engine/signals"
              element={
                <ProtectedRoute>
                  <EngineSignals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/engine/positions"
              element={
                <ProtectedRoute>
                  <EnginePositions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/engine/runs"
              element={
                <ProtectedRoute>
                  <EngineRuns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/engine/learning"
              element={
                <ProtectedRoute>
                  <SingleSymbolLearning />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dego-scan"
              element={
                <ProtectedRoute>
                  <DegoScanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/atr-fibonacci-scan"
              element={
                <ProtectedRoute>
                  <AtrFibonacciScanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tyler-scan"
              element={
                <ProtectedRoute>
                  <TylerScanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rsi-scan"
              element={
                <ProtectedRoute>
                  <RsiScanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vp-supertrend"
              element={
                <ProtectedRoute>
                  <DevkOnlyRoute>
                    <VpSupertrendPage />
                  </DevkOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/v18-inspector"
              element={
                <ProtectedRoute>
                  <V18InspectorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kronos"
              element={
                <ProtectedRoute>
                  <KronosStatusPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Navigate to="/login" replace />
                </PublicRoute>
              }
            />
            <Route
              path="*"
              element={
                <Box sx={{ p: 3 }}>
                  <Typography variant="h4">404 - Page Not Found</Typography>
                </Box>
              }
            />
          </Routes>
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;
